/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect } from 'react-redux';

import { getCurrentLocaleLanguageCode } from '../app/appAuthorized/redux/reduxSelectors';
import { injectIntl, InjectedIntlProps } from 'react-intl';

/** To be returned from the backend */
export interface LocalizedText {
  i18n: {
    [ locale: string ]: string;
  };
  default?: string;
}

/** React components decorated with withLanguageCode should have their Props extends this interface */
export interface HasLanguageCode {
  languageCode?: string;
}

const getLanguageCodeFromState = (state) => ({
  languageCode: getCurrentLocaleLanguageCode(state),
});

/** HOC to wrap React components that needs to get current language code */
export function withLanguageCode(component) {
  return connect(getLanguageCodeFromState)(component);
}

/** Type-skipping variant of @injectIntl */
type ComponentConstructor<P> = React.ComponentClass<P> | React.StatelessComponent<P>;

export function withIntl<P extends { intl?: any }>(component: ComponentConstructor<P>): any {
  return injectIntl(component as any);
}

/** React component to renders LocalizedText in current language */
export const LocalText = withLanguageCode(
  ({ text, languageCode }: { text: LocalizedText, languageCode: string }) => {
    return <span>{getLocalText(text, languageCode)}</span>;
  },
);

/** Get LocalizedText in current language */
export function getLocalText(text: LocalizedText, languageCode: string): string {
  if (!text) return '';

  if (text.i18n[languageCode]) {
    return text.i18n[languageCode];
  }

  if (text.default) {
    return text.default;
  }

  const keys = Object.keys(text.i18n);
  return keys.length ? text.i18n[keys[0]] : '';
}

export interface PhraseData {
  id: string;
  values?: { [key: string]: any };
}

export interface Locale {
  default_locale_medli_key: string;
  default_locale_medli_language_code: string;
  default_medli_locale_id: string;
  id: number;
  key: string;
  language_code: string;
  language_region: string;
  name: string;
}
